/**
 * 屏幕档位（Screen Tier）—— 全站唯一事实来源
 *
 * ┌── 档位与密度的分工（两者正交，互不覆盖）──────────────────────┐
 * │ 档位（本文件 + styles/tier-tokens.css）→ 管「结构」            │
 * │   侧栏形态与宽度、内容最大宽度、kv 网格列数、行高、触控热区      │
 * │ 密度（hooks/useDensity.ts）→ 管「字号」                        │
 * │   html { font-size } 由档位派生(B 16/A 14/C 14/D 14)        │
 * │   → 全部 rem token 随之缩放（2026-10-19 起无用户开关） │
 * └───────────────────────────────────────────────────────────────┘
 *
 * 档位边界与 styles/tokens.css 既有断点阶梯刻意对齐
 * （≤767 / ≤1023 / ≤1279 / ≥1920），因此接入档位层不会改变任何现有数值。
 *
 * 四档（docs/UI设计规范/01-响应式变量矩阵.md）：
 *   B ≥1920        超大屏 / 21:9 / 4K —— 加列 + 内容钳制，不拉伸字段
 *   A 1280–1919    常规笔记本 —— 基准档，其余档位由它推导
 *   C 768–1279     iPad 竖/横 —— 触控优先，热区 ≥40px
 *   D <768         手机 —— 仅保证可读可操作，单列堆叠
 *
 * ⚠️ 设计文档内部对 A 档起点有三种写法（1280 / 1366 / 1440）。本文件取 1280，
 *    理由是与项目已在跑的列数阶梯（≥1280 起 5 列）一致，零回归。
 *    若后续要改成 1366/1440，改 SCREEN_TIER_BOUNDS 一处即可，
 *    同时需同步 styles/tier-tokens.css 的媒体查询回退块。
 *
 * 组件不得写裸断点：CSS 侧引用 --tier-*（styles/tier-tokens.css），
 * JS 侧用 useScreenTier()（hooks/useScreenTier.ts）。
 */

/** 档位边界（px）。语义为「上界」，即 width <= 该值即归入该档。 */
export const SCREEN_TIER_BOUNDS = {
  /** ≤767 → D 手机 */
  dMax: 767,
  /** 768–1279 → C iPad */
  cMax: 1279,
  /** 1280–1919 → A 常规笔记本（基准） */
  aMax: 1919,
  /** ≥1920 → B 超大屏（无上界） */
} as const;

export type ScreenTier = 'A' | 'B' | 'C' | 'D';

/** 档位中文标签（用于设计走查面板 / 调试输出） */
export const SCREEN_TIER_LABELS: Record<ScreenTier, string> = {
  A: 'A 常规笔记本',
  B: 'B 超大屏',
  C: 'C iPad',
  D: 'D 手机',
};

/** 由宽到窄的档位顺序 */
export const SCREEN_TIER_ORDER: readonly ScreenTier[] = ['B', 'A', 'C', 'D'];

/**
 * 各档位下工具栏**直接平铺显示**的业务按钮数量；超出的收进「更多」。
 *
 * 依据：档位由屏幕宽度派生（见 resolveScreenTier），所以这一张表同时决定了
 * iPad / 笔记本 / 超大屏三种形态下工具栏的按钮密度。
 * 变的是「几个按钮直接可见」，**不是**「哪些按钮可用」—— 被收起的按钮
 * 一律进「更多」下拉，仍可点（不得让任何按钮失去可达性）。
 *
 * 注意：`leadingButtons`（返回/刷新这类导航按钮）不受此限制，始终平铺。
 */
export const TIER_VISIBLE_ACTIONS: Record<ScreenTier, number> = {
  B: Number.POSITIVE_INFINITY, // 超大屏（≥1920）：全部平铺
  A: 9, // 笔记本（1280–1919）
  C: 6, // iPad（768–1279）
  D: 6, // 手机（<768）：与 iPad 同口径
};

/** 纯函数：视口宽度 → 档位。无副作用，可单测。 */
export function resolveScreenTier(width: number): ScreenTier {
  if (width > SCREEN_TIER_BOUNDS.aMax) return 'B';
  if (width > SCREEN_TIER_BOUNDS.cMax) return 'A';
  if (width > SCREEN_TIER_BOUNDS.dMax) return 'C';
  return 'D';
}

/** 挂在 <html> 上的档位属性名 —— CSS 侧 styles/tier-tokens.css 依赖它 */
const TIER_ATTRIBUTE = 'data-tier';

let initialized = false;
let currentTier: ScreenTier | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => { listener(); });
}

function apply(tier: ScreenTier): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(TIER_ATTRIBUTE, tier);
}

/**
 * 读取当前档位。优先取 <html data-tier>（可能被设计走查手动指定），
 * 否则按视口宽度实时解析。
 */
export function getScreenTier(): ScreenTier {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute(TIER_ATTRIBUTE);
    if (attr === 'A' || attr === 'B' || attr === 'C' || attr === 'D') {
      return attr;
    }
  }
  if (typeof window === 'undefined') return 'A';
  return resolveScreenTier(window.innerWidth);
}

/** 手动指定档位（设计走查 / Storybook 用）。传 'auto' 恢复按视口自动判定。 */
export function setScreenTierOverride(tier: ScreenTier | 'auto'): void {
  if (typeof document === 'undefined') return;
  if (tier === 'auto') {
    document.documentElement.removeAttribute(TIER_ATTRIBUTE);
    syncFromViewport();
    return;
  }
  currentTier = tier;
  apply(tier);
  notify();
}

function syncFromViewport(): void {
  if (typeof window === 'undefined') return;
  const next = resolveScreenTier(window.innerWidth);
  if (next === currentTier) return;
  currentTier = next;
  apply(next);
  notify();
}

/**
 * 全局档位初始化。
 *
 * 与 initDensity() 同一套路：在 app.tsx 模块顶层同步调用，
 * 保证早于任何页面渲染（含登录页）写入 <html data-tier>，
 * 避免首帧用错档位。幂等，重复调用无副作用。
 */
export function initScreenTier(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  syncFromViewport();
  // 跨断点时只写一次属性并通知订阅者，不做任何布局计算
  window.addEventListener('resize', syncFromViewport, { passive: true });
}

/** 订阅档位变化，返回退订函数。供 useSyncExternalStore 使用。 */
export function subscribeScreenTier(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}